import React, { useState } from 'react';
import { useStudioStore } from '../../stores/useStudioStore';
import { ProductCard } from './ProductCard';
import { studioApiService } from '../../services/studioApiService';

export const PanelProducts: React.FC = () => {
  const { mainQueue, addMainProduct, updateMainProduct, openaiKey, prompt, garmentType, quality, size } = useStudioStore();
  const [isGenerating, setIsGenerating] = useState(false);

  // Filter out products that are already in the queue or generated (we only want 'pending' to be displayed in Panel B)
  const pendingProducts = mainQueue.filter(p => p.status === 'pending');
  const generatingCount = mainQueue.filter(p => ['generating', 'awaiting-approval', 'upscaling'].includes(p.status)).length;
  const totalCount = mainQueue.length;

  const handleAddProduct = () => {
    addMainProduct({
      id: crypto.randomUUID(),
      productId: '',
      images: [],
      status: 'pending',
      reviewState: 'pending'
    });
  };

  const handleGenerateAll = async () => {
    if (!openaiKey) {
      alert("Please enter an OpenAI API key");
      return;
    }
    setIsGenerating(true);

    // We process each pending product
    for (const p of pendingProducts) {
      if (p.images.length === 0 && !p.modelImage) continue;

      updateMainProduct(p.id, { status: 'generating' });

      try {
        const allImages = [...p.images];
        if (p.modelImage) allImages.push(p.modelImage);

        const res = await studioApiService.generateImages(
          openaiKey,
          prompt,
          garmentType,
          quality,
          size,
          allImages
        );
        
        // Since we are mocking the exact OpenAI multipart response structure handling, 
        // assuming our proxy returned { data: [{ b64_json: '...' }] } 
        // We'll store this full grid as the result.
        // In the original, the base64 was extracted and stored in `result.fullSrc`.
        const b64 = res?.data?.[0]?.b64_json;
        if (b64) {
          updateMainProduct(p.id, {
            status: 'awaiting-approval',
            result: { fullSrc: b64 }
          });
        } else {
          updateMainProduct(p.id, { status: 'error', error: 'No image returned' });
        }
      } catch (e: any) {
        updateMainProduct(p.id, { status: 'error', error: e.message });
      }
    }
    
    setIsGenerating(false);
  };

  return (
    <div className="panel" id="panelB">
      <div className="panel-scroll">
        <div id="productCards">
          {mainQueue.map((product, i) => (
          <ProductCard key={product.id} product={product} index={i + 1} />
        ))}
        </div>

        <div style={{ padding: '14px 20px 20px' }}>
          <button className="add-product-btn" onClick={handleAddProduct}>+ Add more products</button>
        </div>
      </div>

      <div className="run-controls">
        <div className="progress-bar-wrap" style={{ display: generatingCount > 0 ? 'block' : 'none' }}>
          <div className="progress-bar-track">
            <div className="progress-bar-fill" style={{ width: `${(generatingCount / totalCount) * 100}%` }}></div>
          </div>
          <div className="progress-text">{totalCount - generatingCount} / {totalCount} completed</div>
        </div>
        <div className="run-btn-row">
          <button 
            className="run-btn primary" 
            onClick={handleGenerateAll}
            disabled={isGenerating || pendingProducts.length === 0}
          >
            {isGenerating && <div className="spinner" style={{ display: 'block' }}></div>}
            <span>▶ Generate all</span>
          </button>
          <button className="run-btn secondary" disabled>⏸</button>
          <button className="run-btn danger" disabled>■</button>
        </div>
      </div>
    </div>
  );
};
